import * as React from "react";

// MUI
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import AddIcon from "@mui/icons-material/Add";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import { grey } from "@mui/material/colors";

// Third Party
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { useQuery, useMutation } from "@apollo/client";

// Internal
import Post from "./components/Post";
import SharedCompose from "../../components/SharedCompose";
import SharedAppBar from "../../components/SharedAppBar";
import {
  GET_PUBLIC_POSTS,
  GET_USER_COLLECTIONS,
} from "../../data/graphql/queries";
import {
  CREATE_POST,
  REACT_POST,
  COMMENT_POST,
  FLAG_POST,
  CREATE_COLLECTION,
  ADD_ITEM_TO_COLLECTION,
} from "../../data/graphql/mutations";
import { GET_USER_PROFILE_BY_ID } from "../../data/graphql/queries";

import {
  PostType,
  ReactionTypeEnum,
  UserType,
  CollectionType,
} from "../../data/types";
import withRoot from "../../withRoot";

import en from "../../data/en.json";

function Main() {
  const { user: auth0User } = useAuth0();
  const { data: publicPostsData } = useQuery(GET_PUBLIC_POSTS, {
    variables: {
      user_auth0_id: auth0User?.sub,
      paging_offset: 0,
      paging_limit: 1000,
    },
  });
  const { data: UserProfileData } = useQuery(GET_USER_PROFILE_BY_ID, {
    variables: { user_auth0_id: auth0User?.sub },
  });

  const {
    loading: collectionsLoading,
    data: collectionsData,
    refetch: refetchCollections,
  } = useQuery(GET_USER_COLLECTIONS, {
    variables: {
      user_auth0_id: auth0User?.sub,
      include_private: true,
    },
  });

  const [createPost, { data: CreatePostData, error: CreatePostError }] =
    useMutation(CREATE_POST);

  const [reactPost, { data: ReactPostData, error: ReactPostError }] =
    useMutation(REACT_POST);

  const [commentPost, { data: CommentPostData, error: CommentPostError }] =
    useMutation(COMMENT_POST);

  const [flagPost, { error: FlagPostError, data: FlagPostData }] =
    useMutation(FLAG_POST);

  const [createCollection, { loading: createCollectionLoading }] =
    useMutation(CREATE_COLLECTION);

  const [addItemToCollection, { loading: addItemLoading }] = useMutation(
    ADD_ITEM_TO_COLLECTION
  );

  const [publicPosts, setPublicPosts] = React.useState<PostType[]>([]);
  const [user, setUser] = React.useState<UserType | null>(null);
  const [snackbarMessage, setSnackbarMessage] = React.useState<null | string>(
    null
  );

  // Collection-related state
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedPost, setSelectedPost] = React.useState<PostType | null>(null);
  const [isCreatingCollection, setIsCreatingCollection] = React.useState(false);
  const [addingToCollectionId, setAddingToCollectionId] = React.useState<
    string | null
  >(null);
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [newCollectionTitle, setNewCollectionTitle] = React.useState("");
  const [newCollectionDescription, setNewCollectionDescription] =
    React.useState("");
  const [newCollectionPublic, setNewCollectionPublic] = React.useState(false);

  // helper function
  const updatePublicPosts = (post: PostType) => {
    let index = publicPosts.findIndex((obj) => obj.id === post.id);
    if (index !== -1) {
      const updatedPublicPosts = [...publicPosts];
      updatedPublicPosts[index] = post;
      setPublicPosts(updatedPublicPosts);
    }
  };

  React.useEffect(() => {
    if (UserProfileData) {
      setUser(UserProfileData.userProfileById as UserType);
    }
  }, [UserProfileData]);

  React.useEffect(() => {
    if (CreatePostData) {
      const newPost: PostType = CreatePostData.createPost as PostType;
      const updatedPublicPosts = [newPost, ...publicPosts];
      setPublicPosts(updatedPublicPosts);
      setSnackbarMessage(en.community.postSuccess);
    }
  }, [CreatePostData]);

  React.useEffect(() => {
    if (publicPostsData) {
      setPublicPosts(publicPostsData.publicPosts as PostType[]);
    }
  }, [publicPostsData]);

  React.useEffect(() => {
    if (ReactPostData) {
      let post: PostType = ReactPostData.reactPost as PostType;
      updatePublicPosts(post);
      setSnackbarMessage(en.community.reactPostSuccess);
    }
  }, [ReactPostData]);

  React.useEffect(() => {
    if (CommentPostData) {
      let post: PostType = CommentPostData.commentPost as PostType;
      updatePublicPosts(post);
      setSnackbarMessage(en.community.commentPostSuccess);
    }
  }, [CommentPostData]);

  React.useEffect(() => {
    if (FlagPostData) {
      let post: PostType = FlagPostData.flagPost as PostType;
      let updatedPublicPosts = publicPosts.filter((obj) => obj.id !== post.id);
      setPublicPosts(updatedPublicPosts);
      setSnackbarMessage(en.community.flagPostSuccess);
    }
  }, [FlagPostData]);

  React.useEffect(() => {
    if (CreatePostError) {
      setSnackbarMessage(en.community.postFailure);
    }
    if (ReactPostError) {
      setSnackbarMessage(en.community.reactPostFailure);
    }
    if (CommentPostError) {
      setSnackbarMessage(en.community.commentPostFailure);
    }
    if (FlagPostError) {
      setSnackbarMessage(en.community.flagPostFailure);
    }
  }, [CreatePostError, ReactPostError, CommentPostError, FlagPostError]);

  // Collection handlers
  const handleAddToCollectionClick = (
    event: React.MouseEvent<HTMLElement>,
    post: PostType
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedPost(post);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedPost(null);
  };

  const handleSelectCollection = async (collection: CollectionType) => {
    if (!selectedPost) {
      console.error("No post selected");
      return;
    }

    setAddingToCollectionId(collection.id);

    try {
      const result = await addItemToCollection({
        variables: {
          collection_uuid: collection.uuid,
          item_type: "Post",
          item_id: selectedPost.id,
        },
      });

      if (result.data?.addItemToCollection?.collectionItem) {
        console.log(
          `Successfully added post to collection "${collection.title}"`
        );
        await refetchCollections();
      } else if (result.data?.addItemToCollection?.errors?.length > 0) {
        console.error(
          "Error adding to collection:",
          result.data.addItemToCollection.errors
        );
      }
    } catch (error) {
      console.error("Error adding post to collection:", error);
    } finally {
      setAddingToCollectionId(null);
    }

    handleCloseMenu();
  };

  const handleCreateNewCollection = () => {
    if (!selectedPost) return;

    setNewCollectionTitle(`Collection - Post ${selectedPost.id}`);
    setNewCollectionDescription("");
    setNewCollectionPublic(false);
    setShowCreateModal(true);
    setAnchorEl(null);
  };

  const handleCreateCollection = async () => {
    if (!selectedPost || !newCollectionTitle.trim()) return;

    setIsCreatingCollection(true);

    try {
      const variables: any = {
        user_auth0_id: auth0User?.sub,
        title: newCollectionTitle.trim(),
        description: newCollectionDescription.trim() || null,
        public: newCollectionPublic,
      };

      if (selectedPost && selectedPost.id) {
        variables.item_type = "Post";
        variables.item_id = selectedPost.id;
      }

      const result = await createCollection({
        variables,
      });

      if (result.data?.createCollection) {
        console.log(
          "Collection created successfully:",
          result.data.createCollection
        );
        await refetchCollections();
        setShowCreateModal(false);
        setNewCollectionTitle("");
        setNewCollectionDescription("");
        setNewCollectionPublic(false);
        setSelectedPost(null);
      }
    } catch (error) {
      console.error("Error creating collection:", error);
    } finally {
      setIsCreatingCollection(false);
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setNewCollectionTitle("");
    setNewCollectionDescription("");
    setNewCollectionPublic(false);
    setSelectedPost(null);
  };

  const handleCreatePost = (text: string, imageUrl: string | null) => {
    if (auth0User) {
      createPost({
        variables: {
          user_auth0_id: auth0User.sub,
          text: text,
          image_url: imageUrl,
        },
      });
    }
  };

  const handleReactPost = (postId: string) => {
    if (auth0User) {
      reactPost({
        variables: {
          user_auth0_id: auth0User.sub,
          reaction_type: ReactionTypeEnum.love,
          post_id: postId,
        },
      });
    }
  };

  const handleCommentPost = (postId: string, commentText: string) => {
    if (auth0User) {
      commentPost({
        variables: {
          user_auth0_id: auth0User.sub,
          post_id: postId,
          comment: commentText,
        },
      });
    }
  };

  const handleFlagPost = (postId: string) => {
    if (auth0User) {
      flagPost({
        variables: {
          user_auth0_id: auth0User.sub,
          post_id: postId,
        },
      });
    }
  };

  if (!auth0User) {
    return <p>{en.common["loading.."]}</p>;
  }

  const userCollections: CollectionType[] =
    (collectionsData?.userCollections as CollectionType[]) || [];

  return (
    <React.Fragment>
      <Snackbar
        open={snackbarMessage !== null}
        autoHideDuration={3000}
        message={snackbarMessage}
        onClose={() => setSnackbarMessage(null)}
      />
      <SharedAppBar returnUrl="/" />
      <Grid container justifyContent={"center"} direction="row" sx={{ mt: 5 }}>
        <Box
          alignItems="flex-start"
          sx={{
            borderLeft: 1,
            borderRight: 1,
            borderColor: grey[200],
            width: 750,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              padding: 2,
              paddingTop: 10,
              borderBottom: 1,
              borderColor: grey[200],
            }}
          >
            {en.common.home}
          </Typography>
          {auth0User && (
            <SharedCompose user={user} handleCreatePost={handleCreatePost} />
          )}

          {publicPosts.map((postData: PostType, key: number) => (
            <Post
              key={key}
              user={auth0User}
              post={postData}
              handleReactPost={handleReactPost}
              handleCommentPost={handleCommentPost}
              handleFlagPost={handleFlagPost}
              handleAddToCollectionClick={handleAddToCollectionClick}
            />
          ))}
        </Box>
      </Grid>

      {/* Collection Selection Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          style: {
            maxHeight: 400,
            width: 250,
          },
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ px: 2, py: 1, fontWeight: "bold" }}
        >
          Select Collection
        </Typography>
        {userCollections.map((collection: CollectionType) => (
          <MenuItem
            key={collection.id}
            onClick={() => handleSelectCollection(collection)}
            disabled={addingToCollectionId === collection.id}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <ListItemText
              primary={collection.title}
              secondary={`${collection.itemsCount} items`}
            />
            {addingToCollectionId === collection.id && (
              <CircularProgress size={20} />
            )}
          </MenuItem>
        ))}
        <MenuItem
          onClick={handleCreateNewCollection}
          sx={{
            borderTop: `1px solid ${grey[200]}`,
          }}
        >
          <AddIcon sx={{ mr: 1 }} />
          <ListItemText primary="Create New Collection" />
        </MenuItem>
      </Menu>

      {/* Create Collection Modal */}
      <Modal
        open={showCreateModal}
        onClose={handleCloseCreateModal}
        aria-labelledby="create-collection-modal"
        aria-describedby="create-collection-form"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            border: "1px solid #ccc",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h6" component="h2">
              Create New Collection
            </Typography>
            <IconButton onClick={handleCloseCreateModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedPost && (
            <Box
              mb={3}
              p={2}
              sx={{ backgroundColor: grey[50], borderRadius: 1 }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Adding post:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {selectedPost.text.substring(0, 50)}...
              </Typography>
            </Box>
          )}

          <Box
            component="form"
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            <TextField
              label="Collection Title"
              value={newCollectionTitle}
              onChange={(e) => setNewCollectionTitle(e.target.value)}
              fullWidth
              required
              variant="outlined"
              disabled={isCreatingCollection}
            />

            <TextField
              label="Description (Optional)"
              value={newCollectionDescription}
              onChange={(e) => setNewCollectionDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              disabled={isCreatingCollection}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={newCollectionPublic}
                  onChange={(e) => setNewCollectionPublic(e.target.checked)}
                  disabled={isCreatingCollection}
                />
              }
              label="Make this collection public"
            />

            <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <Button
                onClick={handleCloseCreateModal}
                disabled={isCreatingCollection}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateCollection}
                disabled={isCreatingCollection || !newCollectionTitle.trim()}
                variant="contained"
                startIcon={isCreatingCollection ? null : <AddIcon />}
              >
                {isCreatingCollection ? "Creating..." : "Create Collection"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </React.Fragment>
  );
}

export default withAuthenticationRequired(withRoot(Main), {
  onRedirecting: () => <div>{en.common["loading.."]}</div>,
});
